import { useTranslation } from 'react-i18next';
import { FREIGHT_OPTIONS as OPTIONS, type FreightChoiceDraft } from '../../../utils/freightChoice';

/**
 * The freight question asked at invoicing when neither the customer nor its
 * head office says what freight it pays (Rob, 2026-10-16). Without it the
 * invoice silently took the delivery zone's fee, which is often nothing.
 * Shared by the order page's Create Invoice form and FreightChoiceDialog.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export function FreightChoiceFields({ value, onChange, customerName, idPrefix = 'freight-choice' }: {
  value: FreightChoiceDraft;
  onChange: (next: FreightChoiceDraft) => void;
  customerName: string;
  idPrefix?: string;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'FreightChoiceFields' });
  const set = (patch: Partial<FreightChoiceDraft>) => onChange({ ...value, ...patch });
  const showFlat = value.option === 'flat' || value.option === 'both';
  const showPct = value.option === 'pct' || value.option === 'both';

  return (
    <fieldset className="space-y-3 min-w-0">
      <legend className="text-sm font-medium text-neutral-800 break-words">{t('question', { customer: customerName })}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OPTIONS.map((o) => (
          <label key={o} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer min-w-0 ${value.option === o ? 'border-primary-500 bg-primary-50' : 'border-neutral-200'}`}>
            <input type="radio" name={`${idPrefix}-option`} className="mt-0.5 shrink-0" checked={value.option === o} onChange={() => set({ option: o })} />
            <span className="min-w-0 break-words">
              <span className="block font-medium text-neutral-800">{t(`option.${o}`)}</span>
              <span className="block text-xs text-neutral-500">{t(`hint.${o}`)}</span>
            </span>
          </label>
        ))}
      </div>
      {(showFlat || showPct) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {showFlat && (
            <div className="min-w-0">
              <label htmlFor={`${idPrefix}-flat`} className="block text-xs font-medium text-neutral-600 mb-1">{t('flatLabel')}</label>
              <input id={`${idPrefix}-flat`} type="number" min="0" step="0.01" inputMode="decimal" value={value.flat}
                onChange={(e) => set({ flat: e.target.value })} className={inputCls} placeholder="0.00" />
            </div>
          )}
          {showPct && (
            <div className="min-w-0">
              <label htmlFor={`${idPrefix}-pct`} className="block text-xs font-medium text-neutral-600 mb-1">{t('pctLabel')}</label>
              <input id={`${idPrefix}-pct`} type="number" min="0" max="100" step="0.1" inputMode="decimal" value={value.pct}
                onChange={(e) => set({ pct: e.target.value })} className={inputCls} placeholder="0" />
            </div>
          )}
          <div className="min-w-0">
            <label htmlFor={`${idPrefix}-label`} className="block text-xs font-medium text-neutral-600 mb-1">{t('labelLabel')}</label>
            <input id={`${idPrefix}-label`} value={value.label} maxLength={60}
              onChange={(e) => set({ label: e.target.value })} className={inputCls} placeholder={t('labelPlaceholder')} />
          </div>
        </div>
      )}
      <label className="flex items-start gap-2 text-sm text-neutral-700">
        <input type="checkbox" className="mt-0.5 shrink-0" checked={value.save} onChange={(e) => set({ save: e.target.checked })} />
        <span className="min-w-0 break-words">{t('save', { customer: customerName })}</span>
      </label>
      {!value.save && <p className="text-xs text-neutral-500 break-words">{t('thisOrderOnly')}</p>}
    </fieldset>
  );
}
